import { memo, useCallback, useState, useMemo } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";
import { useRouter } from "@/application/stores/routerStore";
import { useSessionStore } from "@/application/stores/sessionStore";
import { useRole } from "@/hooks/useRole";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import { clearToken } from "@/infrastructure/auth/authService";
import { colors, spacing, typography, transitions } from "@/design-system/tokens";
import { AppSwitcher } from "./AppSwitcher";

export type NavKey = "warehouses" | "products" | "inventory" | "inventory.stock" | "inventory.kardex" | "inventory.receipts" | "inventory.dispatches" | "inventory.transfers" | "inventory.balance" | "inventory.adjustments" | "analytics" | "analytics.dashboard" | "analytics.reports" | "settings" | "settings.typeUse" | "settings.typeUnit" | "settings.movementSubtype" | "settings.unitConversion" | "settings.category" | "admin" | "admin.users" | "admin.usage";

interface SidenavProps {
  collapsed: boolean;
  activeKey: NavKey;
  onToggle: () => void;
  onNavigate: (key: NavKey) => void;
}

interface NavGroup {
  key: NavKey;
  icon: React.ReactNode;
  children: { key: NavKey; label: (t: any) => string }[];
}

interface NavTopItem {
  key: NavKey;
  icon: React.ReactNode;
}

const TOP_ITEMS: NavTopItem[] = [
  { key: "warehouses", icon: <WarehouseIcon /> },
  { key: "products", icon: <ProductIcon /> },
];

const GROUPS: NavGroup[] = [
  {
    key: "inventory",
    icon: <InventoryIcon />,
    children: [
      { key: "inventory.stock", label: (t: any) => t.platform.stock },
      { key: "inventory.kardex", label: (t: any) => t.platform.kardex },
      { key: "inventory.receipts", label: (t: any) => t.platform.receipts },
      { key: "inventory.dispatches", label: (t: any) => t.platform.dispatches },
      { key: "inventory.transfers", label: (t: any) => t.platform.transfers },
      { key: "inventory.balance", label: (t: any) => t.platform.balance },
      { key: "inventory.adjustments", label: (t: any) => t.platform.adjustments },
    ],
  },
  {
    key: "analytics",
    icon: <AnalyticsIcon />,
    children: [
      { key: "analytics.dashboard", label: (t: any) => t.platform.dashboard },
      { key: "analytics.reports", label: (t: any) => t.platform.reports },
    ],
  },
  {
    key: "settings",
    icon: <SettingsIcon />,
    children: [
      { key: "settings.typeUse", label: (t: any) => t.platform.typeUse },
      { key: "settings.typeUnit", label: (t: any) => t.platform.typeUnit },
      { key: "settings.movementSubtype", label: (t: any) => t.platform.movementSubtype },
      { key: "settings.unitConversion", label: (t: any) => t.platform.unitConversion },
      { key: "settings.category", label: (t: any) => t.platform.category },
    ],
  },
  {
    key: "admin",
    icon: <AdminIcon />,
    children: [
      { key: "admin.users", label: (t: any) => t.platform.users },
      { key: "admin.usage", label: (t: any) => t.platform.usage },
    ],
  },
];

export const Sidenav = memo(function Sidenav({
  collapsed,
  activeKey,
  onToggle,
  onNavigate,
}: SidenavProps) {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const { canSeeNav } = useRole();
  const { analyticsModules } = useTenantUsage();
  const handleLogout = useCallback(() => {
    clearToken();
    navigate("landing");
  }, [navigate]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // Auto-expand the group that contains the active key
    const initial: Record<string, boolean> = { inventory: false, analytics: false, settings: false };
    for (const group of GROUPS) {
      if (activeKey.startsWith(group.key + ".") || activeKey === group.key) {
        initial[group.key] = true;
      }
    }
    return initial;
  });

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  const visibleTopItems = useMemo(() => TOP_ITEMS.filter(item => canSeeNav(item.key)), [canSeeNav]);
  const visibleGroups = useMemo(() => {
    return GROUPS.map(group => ({
      ...group,
      children: group.children.filter(child => {
        if (!canSeeNav(child.key)) return false;
        // Filter analytics children by plan modules (except "reports" which is always visible)
        if (group.key === "analytics" && analyticsModules && analyticsModules.length > 0) {
          const moduleName = child.key.replace("analytics.", "");
          if (moduleName === "reports") return true;
          return analyticsModules.includes(moduleName);
        }
        return true;
      }),
    })).filter(group => canSeeNav(group.key) && group.children.length > 0);
  }, [canSeeNav, analyticsModules]);

  return (
    <aside
      aria-label="Main navigation"
      style={{
        width: collapsed ? "64px" : "220px",
        height: "100vh",
        position: "sticky",
        top: 0,
        background: "#111",
        display: "flex",
        flexDirection: "column",
        transition: `width ${transitions.base}`,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          height: "64px",
          padding: collapsed ? "0" : "0 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <AppLogo />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", overflow: "hidden" }}>
            <AppLogo />
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#fff", letterSpacing: "-0.02em", whiteSpace: "nowrap" }}>Nexo</span>
          </div>
        )}
        <button
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.35)", display: "flex", alignItems: "center", padding: "4px", borderRadius: "6px", transition: `color ${transitions.fast}`, flexShrink: 0 }}
          onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#fff")}
          onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.35)")}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
      </div>

      <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: "2px", overflowY: "auto" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" }}>
          {visibleTopItems.map(item => (
            <TopNavItem
              key={item.key}
              icon={item.icon}
              label={t.platform[item.key as keyof typeof t.platform]}
              active={activeKey === item.key}
              collapsed={collapsed}
              onClick={() => onNavigate(item.key)}
            />
          ))}
        </div>

        {visibleGroups.map(group => {
          const isExpanded = expandedGroups[group.key] ?? true;
          const groupActive = activeKey.startsWith(group.key + ".") || activeKey === group.key;
          return (
            <NavGroupSection
              key={group.key}
              icon={group.icon}
              label={t.platform[group.key as keyof typeof t.platform]}
              expanded={isExpanded}
              collapsed={collapsed}
              groupActive={groupActive}
              onToggle={() => toggleGroup(group.key)}
            >
              {!collapsed && isExpanded && group.children.map(child => (
                <ChildNavItem
                  key={child.key}
                  label={child.label(t)}
                  active={activeKey === child.key}
                  onClick={() => onNavigate(child.key)}
                />
              ))}
            </NavGroupSection>
          );
        })}
      </nav>

      <div style={{ padding: "12px 8px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", flexDirection: "column", gap: "10px", flexShrink: 0 }}>
        <AppSwitcher collapsed={collapsed} />
        <LocaleSwitcher locale={locale} onSwitch={setLocale} collapsed={collapsed} />
        <UserBlock collapsed={collapsed} onLogout={handleLogout} />
      </div>
    </aside>
  );
});

const TopNavItem = memo(function TopNavItem({ icon, label, active, collapsed, onClick }: { icon: React.ReactNode; label: string; active: boolean; collapsed: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      title={collapsed ? label : undefined}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: "12px",
        padding: collapsed ? "10px 0" : "10px 12px",
        justifyContent: collapsed ? "center" : "flex-start",
        background: active ? colors.primary : "none",
        border: "none", borderRadius: "8px", cursor: "pointer",
        color: active ? "#fff" : "rgba(255,255,255,0.5)",
        fontFamily: typography.fontFamily, fontSize: typography.size.sm,
        fontWeight: active ? typography.weight.semibold : typography.weight.medium,
        transition: `all ${transitions.fast}`, whiteSpace: "nowrap", overflow: "hidden", textAlign: "left",
      }}
      onMouseEnter={e => { if (!active) { const el = e.currentTarget as HTMLButtonElement; el.style.background = "rgba(255,255,255,0.07)"; el.style.color = "#fff"; } }}
      onMouseLeave={e => { if (!active) { const el = e.currentTarget as HTMLButtonElement; el.style.background = "none"; el.style.color = "rgba(255,255,255,0.5)"; } }}
    >
      <span style={{ flexShrink: 0, display: "flex" }}>{icon}</span>
      {!collapsed && <span>{label}</span>}
    </button>
  );
});

function NavGroupSection({ icon, label, expanded, collapsed, groupActive, onToggle, children }: { icon: React.ReactNode; label: string; expanded: boolean; collapsed: boolean; groupActive: boolean; onToggle: () => void; children: React.ReactNode }) {
  if (collapsed) {
    return (
      <div title={label} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "10px 0", color: groupActive ? "#fff" : "rgba(255,255,255,0.35)" }}>
        <span style={{ display: "flex" }}>{icon}</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", marginTop: "8px" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px",
          background: "none", border: "none", borderRadius: "8px", cursor: "pointer",
          color: groupActive ? "#fff" : "rgba(255,255,255,0.45)",
          fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold,
          transition: `all ${transitions.fast}`, textAlign: "left",
        }}
        onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.background = "rgba(255,255,255,0.05)"; el.style.color = "#fff"; }}
        onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.background = "none"; el.style.color = groupActive ? "#fff" : "rgba(255,255,255,0.45)"; }}
      >
        <span style={{ flexShrink: 0, display: "flex" }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: `transform ${transitions.fast}`, transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", opacity: 0.5 }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {expanded && (
        <div style={{ display: "flex", paddingLeft: "20px", marginTop: "2px" }}>
          <div style={{ width: "1px", background: "rgba(255,255,255,0.12)", borderRadius: "1px", marginRight: "12px", flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "1px", flex: 1 }}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

function ChildNavItem({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: "10px",
        padding: "8px 10px", background: active ? "rgba(255,255,255,0.07)" : "none",
        border: "none", borderRadius: "7px", cursor: "pointer",
        fontFamily: typography.fontFamily, fontSize: typography.size.sm,
        fontWeight: active ? typography.weight.semibold : typography.weight.medium,
        color: active ? "#fff" : "rgba(255,255,255,0.45)",
        transition: `all ${transitions.fast}`, textAlign: "left", whiteSpace: "nowrap",
      }}
      onMouseEnter={e => { if (!active) { const el = e.currentTarget as HTMLButtonElement; el.style.background = "rgba(255,255,255,0.05)"; el.style.color = "#fff"; } }}
      onMouseLeave={e => { if (!active) { const el = e.currentTarget as HTMLButtonElement; el.style.background = "none"; el.style.color = "rgba(255,255,255,0.45)"; } }}
    >
      {active && <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#fff", flexShrink: 0 }} />}
      <span>{label}</span>
    </button>
  );
}

const LOCALES: Locale[] = ["es", "en"];

const LocaleSwitcher = memo(function LocaleSwitcher({ locale, onSwitch, collapsed }: { locale: Locale; onSwitch: (l: Locale) => void; collapsed: boolean }) {
  if (collapsed) {
    return (
      <button
        onClick={() => onSwitch(locale === "en" ? "es" : "en")}
        title={locale.toUpperCase()}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0", background: "none", border: "none", borderRadius: "8px", cursor: "pointer", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, color: "rgba(255,255,255,0.5)", letterSpacing: "0.05em", transition: `color ${transitions.fast}` }}
        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#fff")}
        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.5)")}
      >
        {locale.toUpperCase()}
      </button>
    );
  }

  return (
    <div role="group" aria-label="Language selector" style={{ display: "flex", background: "rgba(255,255,255,0.06)", borderRadius: "8px", padding: "3px", gap: "2px" }}>
      {LOCALES.map(l => (
        <button key={l} onClick={() => onSwitch(l)} aria-pressed={locale === l} style={{ flex: 1, fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, padding: "5px 0", borderRadius: "6px", border: "none", cursor: "pointer", transition: `all ${transitions.fast}`, background: locale === l ? "rgba(255,255,255,0.12)" : "transparent", color: locale === l ? "#fff" : "rgba(255,255,255,0.35)", letterSpacing: "0.05em" }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
});

const UserBlock = memo(function UserBlock({ collapsed, onLogout }: { collapsed: boolean; onLogout: () => void }) {
  const username = useSessionStore(s => s.username) || "User";
  const roleName = useSessionStore(s => s.roleName) || "";
  const initials = username.slice(0, 2).toUpperCase();

  if (collapsed) {
    return (
      <button onClick={onLogout} title={username} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "6px 0", background: "none", border: "none", cursor: "pointer" }}>
        <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#f5c518", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, color: "#111" }}>{initials}</span>
        </div>
      </button>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 4px" }}>
      <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#f5c518", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.bold, color: "#111" }}>{initials}</span>
      </div>
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{username}</div>
        <div style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>{roleName.replace(/_/g, " ")}</div>
      </div>
      <button
        onClick={onLogout}
        aria-label="Logout"
        style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", display: "flex", padding: "4px", borderRadius: "6px", transition: `color ${transitions.fast}` }}
        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#fff")}
        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.3)")}
      >
        <LogoutIcon />
      </button>
    </div>
  );
});

function AppLogo() {
  return (
    <div style={{ width: "28px", height: "28px", background: colors.primary, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-hidden="true">
      <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
        <path d="M3 7L10 3L17 7V13L10 17L3 13V7Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M10 3V17M3 7L17 13M17 7L3 13" stroke="white" strokeWidth="1.1" strokeOpacity="0.55" />
      </svg>
    </div>
  );
}

function WarehouseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
}
function ProductIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>;
}
function InventoryIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
}
function AnalyticsIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 21H3V3" /><path d="M21 7l-6 6-4-4-6 6" /></svg>;
}
function SettingsIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" /></svg>;
}
function AdminIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
}
function LogoutIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;
}
function ChevronLeftIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>;
}
function ChevronRightIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>;
}
